// Importscherm voor notities: plakken of bestanden kiezen, de AI haalt de business cases eruit,
// Furgel controleert de lijst en kiest wat er in Validatie komt.
import { useRef, useState } from "react";
import { C, Chip, Melding, Venster } from "./gedeeld";
import { haalCasesUit, haalBrainstormItemsUit, BRAINSTORM_TYPES, leesBestand, htmlNaarTekst, MAX_TEKENS } from "./notitiesLezen";

// soort "validatie": de AI haalt business cases eruit die als idee in Validatie komen.
// soort "brainstorm": de AI zet alles om in ideeen, acties, vragen en notities in een nieuwe Brainstorm-sessie.
export default function NotitiesImport({ soort = "validatie", bestaandeTitels = [], onImporteer, onSluit }) {
  const brainstorm = soort === "brainstorm";
  const [titel, setTitel] = useState("Notities uit mijn telefoon, " + new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long" }));
  const [tekst, setTekst] = useState("");
  const [bestanden, setBestanden] = useState([]);
  const [stap, setStap] = useState("invoer");
  const [voortgang, setVoortgang] = useState("");
  const [fout, setFout] = useState("");
  const [cases, setCases] = useState([]);
  const [info, setInfo] = useState(null);
  const [valideer, setValideer] = useState(3);
  const [bezig, setBezig] = useState(false);
  const invoer = useRef(null);
  const [sleept, setSleept] = useState(false);

  // Slepen: bestanden worden gelezen, losse tekst (bijvoorbeeld een geselecteerde notitie) komt in het tekstvak
  async function laat(e) {
    e.preventDefault(); setSleept(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    if (dt.files && dt.files.length) { await kiesBestanden(dt.files); return; }
    const platte = dt.getData && dt.getData("text/plain");
    const html = !platte && dt.getData ? dt.getData("text/html") : "";
    const nieuw = (platte || (html ? htmlNaarTekst(html) : "")).trim();
    if (nieuw) setTekst((p) => (p.trim() ? p.replace(/\s+$/, "") + "\n\n" + nieuw : nieuw));
  }
  const boven = (e) => { e.preventDefault(); if (!sleept) setSleept(true); };
  const weg = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSleept(false); };

  const totaal = tekst.length + bestanden.reduce((s, b) => s + b.tekst.length, 0);

  async function kiesBestanden(lijst) {
    setFout("");
    const nieuw = [];
    for (const f of Array.from(lijst || [])) {
      try { nieuw.push({ naam: f.name, tekst: await leesBestand(f) }); }
      catch (e) { setFout(e.message); }
    }
    setBestanden((p) => [...p, ...nieuw]);
    if (invoer.current) invoer.current.value = "";
  }

  async function lees() {
    setFout(""); setStap("bezig");
    try {
      const alles = [tekst, ...bestanden.map((b) => "### " + b.naam + "\n" + b.tekst)].filter((t) => t.trim()).join("\n\n");
      const voortgangTekst = (i, n) => setVoortgang("Deel " + i + " van " + n + " wordt gelezen...");
      const r = brainstorm ? await haalBrainstormItemsUit(alles, { voortgang: voortgangTekst }) : await haalCasesUit(alles, { bestaandeTitels, voortgang: voortgangTekst });
      setInfo(r);
      setCases((brainstorm ? r.items : r.cases).map((c) => ({ ...c, kies: !c.dubbelVan })));
      setStap("controle");
    } catch (e) { setFout(e.message || "Uitlezen mislukt"); setStap("invoer"); }
  }

  const wijzig = (i, veld, w) => setCases((p) => p.map((c, j) => (j === i ? { ...c, [veld]: w } : c)));
  const gekozen = cases.filter((c) => c.kies && (brainstorm ? c.text.trim() : c.titel.trim()));

  async function importeer() {
    setBezig(true); setFout("");
    try { await (brainstorm ? onImporteer(gekozen, titel.trim() || "Notities import") : onImporteer(gekozen, Math.min(valideer, gekozen.length))); onSluit(); }
    catch (e) { setFout(e.message || "Importeren mislukt"); setBezig(false); }
  }

  return (
    <Venster titel={brainstorm ? "Notities importeren naar Brainstorm" : "Notities importeren naar Validatie"} onSluit={onSluit} breed={820}>
      {stap === "invoer" && (
        <div onDragEnter={boven} onDragOver={boven} onDragLeave={weg} onDrop={laat}
          style={{ position: "relative", borderRadius: 8, outline: sleept ? "2px dashed " + C.blue : "2px dashed transparent", outlineOffset: 6, transition: "outline-color .1s" }}>
          {sleept && (
            <div style={{ position: "absolute", inset: 0, zIndex: 5, background: "rgba(10,10,12,0.88)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: C.blue, margin: 0 }}>Laat los om toe te voegen</p>
            </div>
          )}
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.6 }}>
            Sleep notities of bestanden hierheen, plak ze hieronder, of kies bestanden (.txt, .md, .rtf, .html, .docx). {brainstorm ? "De AI zet het om in ideeen, acties, vragen en notities in een nieuwe brainstormsessie, en beantwoordt de vragen kort." : "De AI haalt er de business cases uit en negeert rommel zoals boodschappenlijstjes."}
            Jij controleert de lijst voordat er iets wordt opgeslagen. Telefoonnummers en e-mailadressen worden verwijderd voordat de tekst naar de AI gaat.
          </p>
          <details style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
            <summary style={{ cursor: "pointer", color: C.blue }}>Hoe krijg ik mijn iPhone-notities hier?</summary>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.7 }}>
              <li>Op de Mac kun je tekst uit Notities selecteren en hierheen slepen. Of sleep een bestand uit Finder.</li>
              <li>Open een notitie, tik op de drie puntjes, kies Delen en dan Kopieer. Plak hem hier. Je kunt meerdere notities achter elkaar plakken, met een lege regel ertussen.</li>
              <li>Of stuur jezelf de notitie per mail of AirDrop en plak de tekst, of bewaar hem als tekstbestand en kies het bestand hier.</li>
              <li>Een PDF of schermafbeelding kan ik niet lezen. Kopieer in dat geval de tekst.</li>
            </ul>
          </details>
          <textarea rows={10} value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Plak hier je notities..." />
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "10px 0" }}>
            <input ref={invoer} type="file" multiple accept=".txt,.md,.markdown,.rtf,.html,.htm,.docx,.csv,text/plain" style={{ width: "auto", maxWidth: 280 }} onChange={(e) => kiesBestanden(e.target.files)} />
            {bestanden.map((b, i) => (
              <span key={i} style={{ fontSize: 11 }}><Chip tekst={b.naam} /> <button className="btn bg" style={{ padding: "1px 6px", fontSize: 10 }} onClick={() => setBestanden((p) => p.filter((_, j) => j !== i))}>x</button></span>
            ))}
          </div>
          {fout && <Melding type="fout">{fout}</Melding>}
          {totaal > MAX_TEKENS && <Melding type="let-op">Dit is meer dan {MAX_TEKENS.toLocaleString("nl-NL")} tekens. Alleen het begin wordt gelezen; importeer de rest in een tweede ronde.</Melding>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: C.muted }}>{totaal.toLocaleString("nl-NL")} tekens</span>
            <button className="btn bp" disabled={totaal < 15} onClick={lees}>Lees de notities uit</button>
          </div>
        </div>
      )}

      {stap === "bezig" && <p style={{ fontSize: 13, color: C.muted, padding: "20px 0", textAlign: "center" }}>{voortgang || "Bezig..."}</p>}

      {stap === "controle" && (
        <div>
          {info && info.afgekapt && <Melding type="let-op">Alleen het eerste deel van de tekst is gelezen. Importeer de rest in een tweede ronde.</Melding>}
          {cases.length === 0 ? (
            <Melding type="let-op">{brainstorm ? "Ik vond niets bruikbaars in deze tekst." : "Ik vond geen business cases in deze tekst. Staat er toch een idee in, plak dan de hele notitie met wat meer context."}</Melding>
          ) : (
            <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>{cases.length} {brainstorm ? "items gevonden" : "business cases gevonden"}. Pas aan wat niet klopt en vink af wat niet mee moet.{brainstorm ? "" : " Mogelijke dubbelen staan standaard uit."}</p>
          )}
          {brainstorm && cases.length > 0 && (
            <div style={{ marginBottom: 10 }}><p className="lbl" style={{ marginBottom: 5 }}>Naam van de sessie</p><input value={titel} onChange={(e) => setTitel(e.target.value)} /></div>
          )}
          <div style={{ display: "grid", gap: 8, maxHeight: "48vh", overflowY: "auto", paddingRight: 4 }}>
            {brainstorm && cases.map((c, i) => (
              <div key={i} className="card" style={{ padding: 10, opacity: c.kies ? 1 : 0.55 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="checkbox" style={{ width: "auto" }} checked={c.kies} onChange={(e) => wijzig(i, "kies", e.target.checked)} aria-label="Meenemen" />
                  <select style={{ width: "auto" }} value={c.type} onChange={(e) => wijzig(i, "type", e.target.value)}>
                    {Object.entries(BRAINSTORM_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input style={{ flex: 1, minWidth: 200 }} value={c.text} onChange={(e) => wijzig(i, "text", e.target.value)} />
                </div>
                {c.type === "question" && <textarea rows={2} style={{ marginTop: 6 }} value={c.ai_response} onChange={(e) => wijzig(i, "ai_response", e.target.value)} placeholder="Antwoord van de AI (aan te passen)" />}
              </div>
            ))}
            {!brainstorm && cases.map((c, i) => (
              <div key={i} className="card" style={{ padding: 12, opacity: c.kies ? 1 : 0.55 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                  <input type="checkbox" style={{ width: "auto" }} checked={c.kies} onChange={(e) => wijzig(i, "kies", e.target.checked)} aria-label="Meenemen" />
                  <input style={{ flex: 1, minWidth: 180, fontWeight: 600 }} value={c.titel} onChange={(e) => wijzig(i, "titel", e.target.value)} />
                  {c.onzeker && <Chip tekst="vaag" kleur={C.yellow} />}
                  {c.dubbelVan && <Chip tekst={"lijkt op: " + c.dubbelVan.slice(0, 30)} kleur={C.red} />}
                </div>
                <textarea rows={3} value={c.probleem} onChange={(e) => wijzig(i, "probleem", e.target.value)} />
                <input style={{ marginTop: 6 }} value={c.doelgroep} onChange={(e) => wijzig(i, "doelgroep", e.target.value)} placeholder="Doelgroep (leeg als de notitie dat niet zegt)" />
                {c.fragment && <p style={{ fontSize: 11, color: C.dim, margin: "6px 0 0" }}>Uit notitie: {c.fragment}</p>}
              </div>
            ))}
          </div>
          {fout && <div style={{ marginTop: 10 }}><Melding type="fout">{fout}</Melding></div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
            {brainstorm ? <span /> : <label style={{ fontSize: 12, color: C.muted, display: "flex", gap: 6, alignItems: "center" }}>
              Meteen laten valideren:
              <select style={{ width: "auto" }} value={valideer} onChange={(e) => setValideer(Number(e.target.value))}>
                {[0, 1, 3, 5, 10].map((n) => <option key={n} value={n}>{n === 0 ? "geen, ik doe het zelf" : "de eerste " + n}</option>)}
              </select>
            </label>}
            <span style={{ display: "flex", gap: 8 }}>
              <button className="btn bg" onClick={() => setStap("invoer")}>Terug</button>
              <button className="btn bp" disabled={!gekozen.length || bezig} onClick={importeer}>{bezig ? "Bezig..." : gekozen.length + (brainstorm ? " naar Brainstorm" : " naar Validatie")}</button>
            </span>
          </div>
          {!brainstorm && <p style={{ fontSize: 11, color: C.dim, margin: "8px 0 0" }}>Elke validatie doet webonderzoek en kost AI-budget, daarom start alleen het aantal dat je kiest. De rest kun je later in Validatie starten.</p>}
          {brainstorm && <p style={{ fontSize: 11, color: C.dim, margin: "8px 0 0" }}>Acties maak je daarna in de sessie met een klik om tot taken. Ideeen die je wilt laten valideren kun je daarna via Validatie, Notities importeren toevoegen.</p>}
        </div>
      )}
    </Venster>
  );
}
